/**
 * FinancialStateCard Component
 * Displays the user's financial state as a vertical stepper
 * Shows progression from foundations (budget) to the top (investments)
 */

import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { colors } from '../../theme/colors';
import { typography } from '../../theme/typography';
import { spacing } from '../../theme/spacing';
import type { FinancialStateSummary } from '../../api/types';

interface FinancialStateCardProps {
  stateSummary: FinancialStateSummary;
  showProgression?: boolean;
  compact?: boolean;
}

// Map color names from API to actual colors
const stateColors: Record<string, string> = {
  gray: '#9CA3AF',
  red: '#EF4444',
  orange: '#F97316',
  amber: '#F59E0B',
  blue: '#3B82F6',
  green: '#10B981',
};

// Pyramid levels from bottom (foundations) to top
// Keys match the API progression keys
// Labels are actions to accomplish (not states already achieved)
const pyramidLevels = [
  { key: 'cashflow_unstable', label: 'Équilibrer mon budget', icon: '💰' },
  { key: 'no_emergency_fund', label: 'Constituer mon épargne de sécurité', icon: '🛡️' },
  { key: 'debt_pressure', label: 'Maîtriser mes dettes', icon: '📉' },
  { key: 'cashflow_optimized', label: 'Optimiser mes finances', icon: '📊' },
  { key: 'investment_ready', label: 'Investir pour l\'avenir', icon: '🚀' },
];

export default function FinancialStateCard({
  stateSummary,
  showProgression = true,
  compact = false,
}: FinancialStateCardProps) {
  const stateColor = stateColors[stateSummary.color] || colors.primary;

  // Get status for a pyramid level by matching its key with progression data
  const getStepStatus = (levelKey: string): 'completed' | 'current' | 'pending' => {
    const progressionItem = stateSummary.progression.find(p => p.key === levelKey);
    if (!progressionItem) return 'pending';
    if (progressionItem.status === 'completed') return 'completed';
    if (progressionItem.status === 'current') return 'current';
    return 'pending'; // 'upcoming' maps to 'pending'
  };

  return (
    <View style={styles.container}>
      {/* State Header */}
      <View style={styles.header}>
        <Text style={styles.icon}>{stateSummary.icon}</Text>
        <View style={styles.headerText}>
          <Text style={[styles.label, { color: stateColor }]}>
            {stateSummary.label}
          </Text>
          <Text style={styles.step}>
            Étape {stateSummary.step}/5
          </Text>
        </View>
      </View>

      {/* Description */}
      {!compact && (
        <Text style={styles.description}>{stateSummary.description}</Text>
      )}

      {/* Stepper Progression */}
      {showProgression && (
        <View style={styles.stepperContainer}>
          <Text style={styles.stepperTitle}>Ta progression patrimoniale</Text>

          {/* Stepper levels - from first step (budget) to last (investment) */}
          {pyramidLevels.map((level, index) => {
            const status = getStepStatus(level.key);
            const isCompleted = status === 'completed';
            const isCurrent = status === 'current';
            const isLast = index === pyramidLevels.length - 1;

            return (
              <View key={level.key} style={styles.stepRow}>
                {/* Left column: dot + connecting line */}
                <View style={styles.stepDotColumn}>
                  {/* Dot */}
                  <View
                    style={[
                      styles.stepDot,
                      isCompleted && styles.stepDotCompleted,
                      isCurrent && styles.stepDotCurrent,
                      !isCompleted && !isCurrent && styles.stepDotPending,
                    ]}
                  >
                    {isCompleted && (
                      <Text style={styles.stepCheckmark}>✓</Text>
                    )}
                  </View>
                  {/* Connecting line (not on last item) */}
                  {!isLast && (
                    <View
                      style={[
                        styles.stepLine,
                        isCompleted && styles.stepLineCompleted,
                        !isCompleted && styles.stepLinePending,
                      ]}
                    />
                  )}
                </View>

                {/* Right column: icon + label + badge */}
                <View style={styles.stepContent}>
                  <View style={styles.stepLabelRow}>
                    <Text style={styles.stepIcon}>{level.icon}</Text>
                    <Text
                      style={[
                        styles.stepLabel,
                        isCompleted && styles.stepLabelCompleted,
                        isCurrent && styles.stepLabelCurrent,
                        !isCompleted && !isCurrent && styles.stepLabelPending,
                      ]}
                      numberOfLines={2}
                    >
                      {level.label}
                    </Text>
                  </View>
                  {isCurrent && (
                    <View style={styles.currentBadge}>
                      <Text style={styles.currentBadgeText}>Étape actuelle</Text>
                    </View>
                  )}
                </View>
              </View>
            );
          })}
        </View>
      )}

      {/* Next Step */}
      {!compact && stateSummary.next_step && (
        <View style={styles.nextStepContainer}>
          <Text style={styles.nextStepTitle}>Prochaine étape :</Text>
          <Text style={styles.nextStepAction}>{stateSummary.next_step.action}</Text>
          <Text style={styles.nextStepDescription}>
            {stateSummary.next_step.description}
          </Text>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 12,
    padding: spacing.md,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  icon: {
    fontSize: 32,
    marginRight: spacing.sm,
  },
  headerText: {
    flex: 1,
  },
  label: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
  },
  step: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginTop: 2,
  },
  description: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginBottom: spacing.md,
    lineHeight: 20,
  },
  // Stepper styles
  stepperContainer: {
    marginVertical: spacing.md,
  },
  stepperTitle: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: spacing.md,
  },
  stepRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
  },
  stepDotColumn: {
    width: 24,
    alignItems: 'center',
  },
  stepDot: {
    width: 16,
    height: 16,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 4,
  },
  stepDotCompleted: {
    backgroundColor: '#10B981',
  },
  stepDotCurrent: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: colors.info,
    marginTop: 2,
  },
  stepDotPending: {
    backgroundColor: 'transparent',
    borderWidth: 2,
    borderColor: colors.border,
  },
  stepLine: {
    width: 2,
    flex: 1,
    minHeight: 16,
  },
  stepLineCompleted: {
    backgroundColor: '#10B981',
  },
  stepLinePending: {
    backgroundColor: colors.border,
    borderStyle: 'dashed',
  },
  stepContent: {
    flex: 1,
    marginLeft: spacing.sm,
    paddingBottom: spacing.md,
  },
  stepLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  stepIcon: {
    fontSize: 16,
    marginRight: spacing.xs,
  },
  stepLabel: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    flex: 1,
  },
  stepLabelCompleted: {
    color: '#10B981',
  },
  stepLabelCurrent: {
    color: colors.text,
    fontWeight: typography.fontWeight.bold,
  },
  stepLabelPending: {
    color: colors.textSecondary,
    opacity: 0.6,
  },
  stepCheckmark: {
    fontSize: 10,
    color: colors.background,
    fontWeight: typography.fontWeight.bold,
  },
  currentBadge: {
    backgroundColor: colors.info + '20',
    borderRadius: 4,
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
    alignSelf: 'flex-start',
    marginTop: spacing.xs,
  },
  currentBadgeText: {
    fontSize: typography.fontSize.xs,
    color: colors.info,
    fontWeight: typography.fontWeight.semibold,
  },
  // Next step styles
  nextStepContainer: {
    marginTop: spacing.md,
    paddingTop: spacing.md,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  nextStepTitle: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: spacing.xs,
  },
  nextStepAction: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
    marginBottom: spacing.xs,
  },
  nextStepDescription: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    lineHeight: 18,
  },
});
